/**
 * Staff invitation acceptance for existing accounts.
 *
 * The backend sends an existing user a sign-in link `/admin?staff_token=<token>`
 * (a brand-new user gets `/admin/sign-up?staff_token=...`, handled by Signup.js).
 * After the user logs in, the dashboard must POST the token to
 * `/connect/staff/invitations/<token>/` to create the Staff link.
 *
 * The tricky part: an unauthenticated click is redirected to `/login`, which
 * drops the query string before any authenticated component mounts. So we
 * capture the token from the raw URL on module import — i.e. before React
 * Router runs — and stash it for the post-login effect to consume.
 */

const PENDING_KEY = 'kio_pending_staff_token';
const ERROR_KEY = 'kio_staff_invitation_error';

/** Read staff_token from the current URL and persist it. Returns the token or null. */
export function captureStaffTokenFromUrl() {
  try {
    const token = new URLSearchParams(window.location.search).get('staff_token');
    if (token) localStorage.setItem(PENDING_KEY, token);
    return token || null;
  } catch {
    return null;
  }
}

export function getPendingStaffToken() {
  try {
    return localStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

export function clearPendingStaffToken() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function setStaffInvitationError(message) {
  try {
    localStorage.setItem(ERROR_KEY, message);
  } catch {
    /* ignore */
  }
}

/** Read and clear any stored invitation error (shown once on the landing page). */
export function popStaffInvitationError() {
  try {
    const value = localStorage.getItem(ERROR_KEY);
    if (value) localStorage.removeItem(ERROR_KEY);
    return value;
  } catch {
    return null;
  }
}

// Capture immediately on import, before React Router can redirect and drop the
// query string. Importing this module for its side effect is intentional.
captureStaffTokenFromUrl();
